import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  BreederOverviewGetBreederQuery,
  BreederOverviewGetBreederQueryVariables,
} from '@gql/graphql';
import { useMemo } from 'react';

export const BREEDER_OVERVIEW_GET_BREEDER = graphql(`
  query BreederOverviewGetBreeder(
    $where: DogBreederWhereInput!
    $take: Int
    $breedCardInfoTranslationsWhere: DogBreedTranslationWhereInput!
  ) {
    findManyDogBreeder(where: $where, take: $take) {
      id
      agreementNumber
      websiteUrl
      certificationFile
      level
      spokenLanguages
      breedingName
      profileImg
      lastConnectedType
      imgs {
        id
        small
        type
        large
      }
      translations {
        id
        title
        description
        seoKeywords
        dogsActivities
        puppiesLivingPlace
        dogsLivingPlace
        health
        breederDogsSelection
        puppiesTraining
        url
        language
      }

      account {
        id
        connectedAt
        firstName
        lastName
        address {
          id
          zip
          city
          country
          street
        }
      }

      breederBreeds {
        id
        dogBreed {
          baseBreedName
          name
          translations(where: $breedCardInfoTranslationsWhere) {
            id
            name
            description
            url
            language
          }
        }
      }
    }
  }
`);

export const useGetBreeder = (id?: number) => {
  const { language } = useRouter();

  const variables = useMemo(() => {
    return {
      take: 1,
      where: { id: { equals: id } },
      breedCardInfoTranslationsWhere: { language: { equals: language } },
    };
  }, [id, language]);

  const { data, error, loading } = useQuery<
    BreederOverviewGetBreederQuery,
    BreederOverviewGetBreederQueryVariables
  >(BREEDER_OVERVIEW_GET_BREEDER, {
    variables,
    skip: !id,
  });

  const breeder = data?.findManyDogBreeder?.[0];

  const breederFilteredBreeds = useMemo(() => {
    const breeds = breeder?.breederBreeds ?? [];
    const baseBreedNames = breeds.map((x) => x.dogBreed.baseBreedName ?? undefined);
    return breeds.filter((x) => !baseBreedNames.includes(x.dogBreed.name));
  }, [breeder?.breederBreeds]);

  return {
    breeder,
    breederFilteredBreeds,
    breederError: error,
    breederLoading: loading,
    breederVariables: variables,
  };
};
